import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminEInvoiceService } from "@/services/admin-e-invoice.service";
import type {
  EInvoiceCreatePayload,
  EInvoiceTaxConfig,
  UpdateEInvoiceTaxConfig,
  GetEInvoiceRequestsParams,
} from "@/types/e-invoice-request";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const eInvoiceRequestKeys = {
  all: ["admin-e-invoice-requests"] as const,
  list: (params: GetEInvoiceRequestsParams) =>
    ["admin-e-invoice-requests", "list", params] as const,
  detail: (id: string) => ["admin-e-invoice-requests", "detail", id] as const,
};

// ─── List Query ───────────────────────────────────────────────────────────────

export function useAdminEInvoiceRequests(params: GetEInvoiceRequestsParams) {
  return useQuery({
    queryKey: eInvoiceRequestKeys.list(params),
    queryFn: () => adminEInvoiceService.getRequests(params),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

// ─── Detail Query ─────────────────────────────────────────────────────────────

export function useAdminEInvoiceRequest(id: string, enabled = true) {
  return useQuery({
    queryKey: eInvoiceRequestKeys.detail(id),
    queryFn: () => adminEInvoiceService.getRequest(id),
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
}

// ─── Adjust Mutation ──────────────────────────────────────────────────────────

export function useAdjustEInvoiceRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: EInvoiceCreatePayload;
    }) => adminEInvoiceService.adjustRequest(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: eInvoiceRequestKeys.all });
      queryClient.setQueryData(eInvoiceRequestKeys.detail(data.id), data);
      toast.success("Request payload adjusted successfully.");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to adjust request.");
    },
  });
}

// ─── Approve Mutation ─────────────────────────────────────────────────────────

export function useApproveEInvoiceRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminEInvoiceService.approveRequest(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: eInvoiceRequestKeys.all });
      queryClient.setQueryData(eInvoiceRequestKeys.detail(data.id), data);
      toast.success(
        "Request approved — a DRAFT invoice has been created on the Hub.",
      );
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to approve request.");
    },
  });
}

// ─── Reject Mutation ──────────────────────────────────────────────────────────

export function useRejectEInvoiceRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminEInvoiceService.rejectRequest(id, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: eInvoiceRequestKeys.all });
      queryClient.setQueryData(eInvoiceRequestKeys.detail(data.id), data);
      toast.success("Request rejected.");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reject request.");
    },
  });
}

// ─── Legacy alias (kept for any code still referencing the old hook names) ───

/** @deprecated Use useAdminEInvoiceRequests instead. */
export const useAdminEInvoices = useAdminEInvoiceRequests;

/** @deprecated Use useAdminEInvoiceRequest instead. */
export function useAdminEInvoiceDetail(
  _key: string,
  _orderNumber: string,
  _enabled: boolean,
) {
  // The old signature used (key, orderNumber) but the new API uses request ID.
  // Callers must be updated to useAdminEInvoiceRequest(id).
  void _key;
  void _orderNumber;
  void _enabled;
  return { data: undefined, isLoading: false };
}

export function useAdminDefaultEInvoiceTaxConfig(enabled = true) {
  return useQuery({
    queryKey: ["admin-e-invoice-default-tax-config"],
    queryFn: () => adminEInvoiceService.getDefaultTaxConfig(),
    enabled,
    refetchOnMount: "always",
    staleTime: 60_000,
    retry: false,
  });
}

export function useUpdateAdminDefaultEInvoiceTaxConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (setting: UpdateEInvoiceTaxConfig) =>
      adminEInvoiceService.updateDefaultTaxConfig(setting),
    onSuccess: (data) => {
      queryClient.setQueryData<EInvoiceTaxConfig>(
        ["admin-e-invoice-default-tax-config"],
        data,
      );
      toast.success("Wi-Invoice default configuration saved.");
    },
    onError: (error: Error) =>
      toast.error(
        error.message ?? "Failed to save Wi-Invoice default configuration.",
      ),
  });
}

export function useCreateEInvoiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: EInvoiceCreatePayload;
    }) => adminEInvoiceService.createRequest(orderId, payload),
    onSuccess: async (data) => {
      queryClient.setQueryData(eInvoiceRequestKeys.detail(data.id), data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: eInvoiceRequestKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["admin-orders"] }),
        queryClient.invalidateQueries({
          queryKey: ["admin-order", data.orderId],
        }),
        queryClient.invalidateQueries({ queryKey: ["admin", "billing-info"] }),
      ]);
      toast.success("E-invoice request created successfully.");
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to create e-invoice request."),
  });
}
